import { useState, useEffect } from "react";
import { Box, Grid, CircularProgress } from "@mui/material";
import { useParams } from "react-router-dom";
import { colors } from "../source/source.styles";
import DSPHeader from "./DSPHeader";
import DSPKPICards from "./DSPKPICards";
import DSPSourceTrendChart from "./DSPSourceTrendChart";
import DSPLeadQuality from "./DSPLeadQuality";
import DSPConversionFunnel from "./DSPConversionFunnel";
import DSPLeadsTable from "./DSPLeadsTable";
import { getSourcePerformanceApi } from "../../../api/leads.api";
import dayjs from "dayjs";

const DetailedSourcePerformanceView = () => {
  const { sourceName } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [dateRange, setDateRange] = useState({
    startDate: dayjs().startOf("month").toDate(),
    endDate: dayjs().endOf("month").toDate(),
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = {
        startDate: dateRange.startDate.toISOString(),
        endDate: dateRange.endDate.toISOString(),
      };

      // Add source filter if sourceName is provided
      if (sourceName) {
        params.source = decodeURIComponent(sourceName);
      }

      const response = await getSourcePerformanceApi(params);
      setData(response.data?.data);
    } catch (error) {
      console.error("Error fetching detailed source performance:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    console.log("Exporting PDF...");
  };

  const handleDateRangeChange = (range) => {
    setDateRange(range);
  };

  // Fetch data when dateRange changes
  useEffect(() => {
    fetchData();
  }, [dateRange]);

  if (loading) {
    return (
      <Box
        sx={{
          height: "100vh",
          bgcolor: colors.bgPrimary,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress sx={{ color: colors.primary }} />
      </Box>
    );
  }

  // Calculate quality distribution from sources
  const qualityCounts = data?.sources?.reduce(
    (acc, source) => {
      const label = source.qualityLabel?.toLowerCase();
      if (label === "excellent") acc.excellent++;
      else if (label === "good") acc.good++;
      else if (label === "average") acc.average++;
      else acc.poor++;
      return acc;
    },
    { excellent: 0, good: 0, average: 0, poor: 0 },
  ) || { excellent: 0, good: 0, average: 0, poor: 0 };

  const qualityData = {
    excellent: qualityCounts.excellent,
    good: qualityCounts.good,
    average: qualityCounts.average,
    poor: qualityCounts.poor,
  };

  // Build conversion funnel from pipeline stages
  const sources = data?.sources || [];

  // Calculate total won leads for KPI cards and conversion rate
  const totalWon = sources.reduce((sum, s) => sum + (s.wonLeads || 0), 0);

  const totalLeads = data?.summary?.totalLeads || 0;
  const conversionRate =
    totalLeads > 0 ? Math.round((totalWon / totalLeads) * 1000) / 10 : 0;

  // Use real time-series data from API or generate mock data if not available
  const apiTimeSeriesData = data?.timeSeriesData;
  const isSingleDate =
    dateRange.startDate &&
    dateRange.endDate &&
    new Date(dateRange.startDate).toDateString() ===
      new Date(dateRange.endDate).toDateString();

  let chartData;

  if (apiTimeSeriesData && apiTimeSeriesData.labels?.length > 0) {
    // Use real data from API
    chartData = apiTimeSeriesData;
  } else {
    // Fallback to mock data for display
    if (isSingleDate) {
      const hours = Array.from(
        { length: 24 },
        (_, i) => `${i.toString().padStart(2, "0")}:00`,
      );
      const leadsPerHour = Math.max(totalLeads / 24, 1);
      const baseConvRate = conversionRate || 10;

      chartData = {
        labels: hours,
        leads: hours.map((_, i) => {
          const hour = i;
          if (hour >= 9 && hour <= 17) {
            return Math.round(leadsPerHour * (1.5 + Math.random() * 0.5));
          } else if (hour >= 6 && hour <= 20) {
            return Math.round(leadsPerHour * (0.7 + Math.random() * 0.5));
          } else {
            return Math.round(leadsPerHour * (0.2 + Math.random() * 0.4));
          }
        }),
        conversions: hours.map((_, i) => {
          let rate = baseConvRate;
          rate = rate * (0.8 + Math.random() * 0.4);
          return Math.min(Math.round(rate * 10) / 10, 100);
        }),
      };
    } else {
      const dayDiff =
        Math.ceil(
          (new Date(dateRange.endDate) - new Date(dateRange.startDate)) /
            (1000 * 60 * 60 * 24),
        ) + 1;
      const days = Math.min(dayDiff, 30);
      const labels = [];
      const today = new Date();

      for (let i = days - 1; i >= 0; i--) {
        const d = new Date(today);
        d.setDate(d.getDate() - i);
        labels.push(
          d.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
        );
      }

      const leadsPerDay = totalLeads / days;
      const convPerDay = totalWon / days;
      chartData = {
        labels: labels,
        leads: labels.map(() =>
          Math.round(leadsPerDay * (0.5 + Math.random())),
        ),
        conversions: labels.map(
          () => Math.round(convPerDay * (0.5 + Math.random()) * 100) / 100,
        ),
      };
    }
  }

  // Transform data for components
  const kpiData = {
    totalLeads: {
      value: totalLeads,
      trend: "+12.3% vs last month",
      trendType: "up",
    },
    avgRevenuePerLead: {
      value:
        data?.summary?.totalRevenue && totalLeads
          ? Math.round(data.summary.totalRevenue / totalLeads)
          : 0,
      trend: "+5.2% vs last month",
      trendType: "up",
    },
    conversion: {
      value: conversionRate,
      count: totalWon,
      trend: "+0.3 points since last month",
      trendType: "up",
    },
    revenue: {
      value: data?.summary?.totalRevenue || 0,
      trend: "+8.5% High performing month",
      trendType: "up",
    },
  };

  return (
    <Box
      sx={{
        bgcolor: colors.bgPrimary,
        minHeight: "100%",
      }}
    >
      <DSPHeader
        onExport={handleExport}
        onDateRangeChange={handleDateRangeChange}
        dateRange={dateRange}
        sourceName={sourceName ? decodeURIComponent(sourceName) : null}
      />

      <Box sx={{ p: 4 }}>
        <Box>
          {/* KPI Cards */}
          <Box sx={{ mb: 4 }}>
            <DSPKPICards data={kpiData} />
          </Box>

          {/* Lead Trends Chart */}
          <Box sx={{ mb: 4 }}>
            <DSPSourceTrendChart data={chartData} dateRange={dateRange} />
          </Box>

          {/* Two Column Layout: Quality + Funnel */}
          <Box>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }} sx={{ display: "flex" }}>
                <DSPLeadQuality data={data} />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }} sx={{ display: "flex" }}>
                <DSPConversionFunnel data={data} />
              </Grid>
            </Grid>
          </Box>

          {/* Leads Table - Using real leads data from API */}
          <Box sx={{ mt: 10 }}>
            <DSPLeadsTable data={data?.leads || []} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default DetailedSourcePerformanceView;
